import { useEffect, useMemo } from "react";

import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { Link as RouterLink } from "react-router-dom";

import {
  CARRIER_UPGRADE_RELATIVE_URL,
  ClientAppEvent,
  EVOTRUX_ERROR_RED,
  SHIPPER_UPGRADE_RELATIVE_URL,
} from "constants/index";
import { useRoleContext } from "hooks";
import { useStripeCustomerBillingPortal } from "hooks/useStripeCustomerBillingPortal";
import { useSubscriptionStatus } from "hooks/useSubscriptionStatus";
import { canOpenStripeBillingPortal } from "utils/subscription";
import { buildSupportMailtoLink } from "utils/support";

import {
  LAPSED_SUBSCRIPTION_COPY,
  LAPSED_SUBSCRIPTION_SUPPORT_SUBJECT,
  PRIMARY_BUTTON_SX,
  RECOVERY_ACTION_TOOLTIP,
  SUPPORT_FALLBACK_LABEL,
} from "./constants";
import {
  clearShownCaptured,
  getShownReasonOrNull,
  hasCapturedShown,
  markShownCaptured,
} from "./utils";

import type { LapsedSubscriptionReason, RecoveryAction } from "./constants";

export function LapsedSubscriptionBanner() {
  const { subscription } = useSubscriptionStatus();
  const { company } = useRoleContext();
  const posthog = usePostHog();

  const { loading, error, requestCustomerBillingPortalSession } =
    useStripeCustomerBillingPortal({
      onSessionCreatedSuccess: (url) => {
        window.open(url, "_self");
      },
    });

  const companyId = company?.id;
  const companyType = company?.company_type;
  const isActive = subscription?.is_active === true;
  const reason = subscription?.inactivity_reason;

  // NONE and SUPERSEDED never render. SUPERSEDED in particular means a newer
  // active subscription exists, so the customer is not lapsed at all.
  const lapsedReason: LapsedSubscriptionReason | null =
    !isActive && reason != null && reason !== "NONE" && reason !== "SUPERSEDED"
      ? reason
      : null;

  const analyticsProperties = useMemo(
    () => ({
      inactivity_reason: reason,
      subscription_plan: subscription?.subscription_plan,
      company_type: companyType,
    }),
    [reason, subscription?.subscription_plan, companyType],
  );

  useEffect(() => {
    if (lapsedReason == null || posthog == null || companyId == null) {
      return;
    }
    if (hasCapturedShown(companyId, lapsedReason)) {
      return;
    }
    markShownCaptured(companyId, lapsedReason);
    posthog.capture(
      ClientAppEvent.LAPSED_SUBSCRIPTION_BANNER_SHOWN,
      analyticsProperties,
    );
  }, [lapsedReason, companyId, posthog, analyticsProperties]);

  // A company that recovers goes from a lapsed reason to active in the same
  // payload, so the reason it recovered *from* only survives in the session
  // record the shown event left behind. Clearing that record is also what stops
  // the event firing twice.
  useEffect(() => {
    if (!isActive || posthog == null || companyId == null) {
      return;
    }
    const recoveredFromReason = getShownReasonOrNull(companyId);
    if (recoveredFromReason == null) {
      return;
    }
    clearShownCaptured(companyId);
    posthog.capture(ClientAppEvent.LAPSED_SUBSCRIPTION_RECOVERED, {
      ...analyticsProperties,
      inactivity_reason: recoveredFromReason,
    });
  }, [isActive, companyId, posthog, analyticsProperties]);

  // company gates rendering as well as analytics: the two hydrate from
  // independently restored contexts, and without it a carrier's CTA points at
  // the shipper upgrade page for the window before the role context resolves.
  if (subscription == null || lapsedReason == null || company == null) {
    return null;
  }

  const copy = LAPSED_SUBSCRIPTION_COPY[lapsedReason];
  const upgradeUrl =
    companyType === "CARRIER"
      ? CARRIER_UPGRADE_RELATIVE_URL
      : SHIPPER_UPGRADE_RELATIVE_URL;
  const supportMailtoLink = buildSupportMailtoLink(
    company?.name == null
      ? LAPSED_SUBSCRIPTION_SUPPORT_SUBJECT
      : `${company.name} — ${LAPSED_SUBSCRIPTION_SUPPORT_SUBJECT}`,
  );

  // A billing-portal session needs a Stripe customer, so off-Stripe plans get
  // the support mailto rather than a button that can only fail.
  const fallsBackToSupport =
    copy.primaryAction === "BILLING_PORTAL" &&
    !canOpenStripeBillingPortal(subscription);
  const primaryAction: RecoveryAction = fallsBackToSupport
    ? "SUPPORT_EMAIL"
    : copy.primaryAction;
  const primaryLabel = fallsBackToSupport
    ? SUPPORT_FALLBACK_LABEL
    : copy.primaryLabel;
  // The fallback can turn the primary into the very mailto the secondary link
  // points at, and the banner never offers the same action twice.
  const secondaryLabel =
    primaryAction === "SUPPORT_EMAIL" ? undefined : copy.secondaryLabel;

  const capturePrimaryClick = () => {
    posthog?.capture(ClientAppEvent.LAPSED_SUBSCRIPTION_BANNER_CTA_CLICKED, {
      ...analyticsProperties,
      recovery_action: primaryAction,
    });
  };

  const sharedPrimaryProps = {
    variant: "contained" as const,
    disableElevation: true,
    sx: PRIMARY_BUTTON_SX,
  };

  function renderPrimaryButton() {
    if (primaryAction === "BILLING_PORTAL") {
      return (
        <Button
          {...sharedPrimaryProps}
          disabled={loading}
          endIcon={loading ? <CircularProgress size={16} /> : undefined}
          onClick={() => {
            capturePrimaryClick();
            requestCustomerBillingPortalSession();
          }}
        >
          {primaryLabel}
        </Button>
      );
    }

    if (primaryAction === "SUPPORT_EMAIL") {
      return (
        <Button
          {...sharedPrimaryProps}
          href={supportMailtoLink}
          onClick={capturePrimaryClick}
        >
          {primaryLabel}
        </Button>
      );
    }

    return (
      <Button
        {...sharedPrimaryProps}
        component={RouterLink}
        to={upgradeUrl}
        onClick={capturePrimaryClick}
      >
        {primaryLabel}
      </Button>
    );
  }

  return (
    <Stack
      role="status"
      aria-live="polite"
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      spacing={2}
      sx={{
        position: "sticky",
        top: 0,
        // App-bar level: a banner the user cannot dismiss must not be painted
        // over by the sticky headers inside the page content.
        zIndex: (theme) => theme.zIndex.appBar,
        backgroundColor: EVOTRUX_ERROR_RED,
        minHeight: 56,
        px: 3,
        py: 1,
      }}
    >
      <ErrorIcon aria-hidden sx={{ color: "white", flexShrink: 0 }} />
      <Stack
        direction="row"
        alignItems="baseline"
        flexWrap="wrap"
        columnGap={1.5}
        sx={{ flex: 1, minWidth: 240 }}
      >
        <Typography sx={{ color: "white", fontWeight: "bold" }}>
          {copy.headline}
        </Typography>
        <Typography sx={{ color: "white", opacity: 0.9 }}>
          {copy.body}
        </Typography>
      </Stack>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: "auto" }}>
        <Tooltip title={RECOVERY_ACTION_TOOLTIP[primaryAction]} describeChild>
          {/* The billing-portal button disables itself mid-request, and a
              disabled button fires no hover events, so the tooltip listens on a
              wrapper instead. */}
          <Box component="span" sx={{ display: "inline-flex" }}>
            {renderPrimaryButton()}
          </Box>
        </Tooltip>
        {secondaryLabel != null && (
          <Tooltip title={RECOVERY_ACTION_TOOLTIP.SUPPORT_EMAIL} describeChild>
            <Link
              href={supportMailtoLink}
              underline="always"
              sx={{ color: "white", textDecorationColor: "white" }}
              onClick={() => {
                posthog?.capture(
                  ClientAppEvent.LAPSED_SUBSCRIPTION_BANNER_SECONDARY_CLICKED,
                  analyticsProperties,
                );
              }}
            >
              {secondaryLabel}
            </Link>
          </Tooltip>
        )}
      </Box>
      {error != null && (
        // Not ErrorTypography: it hardcodes red text, which is unreadable on
        // this bar. Opening the portal is the only recovery a PAYMENT_FAILED
        // company has, so a failure has to say so and offer a way through.
        <Typography sx={{ color: "white", width: "100%" }}>
          {error}{" "}
          <Link
            href={supportMailtoLink}
            underline="always"
            sx={{ color: "white", textDecorationColor: "white" }}
          >
            {SUPPORT_FALLBACK_LABEL}
          </Link>
        </Typography>
      )}
    </Stack>
  );
}
